import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MessageSquareText, Save } from 'lucide-react';
import { api } from '../../../api/client';
import { toast } from '../../../lib/toast';
import InfoTip from '../../../components/InfoTip';

const MAX = 600;

// B1: per-domain brand-voice guidance. Injected into the AI rewriter prompt
// for every rewrite on this domain (on top of whatever prompt is in effect),
// so rewrites come out in the user's tone. Available on every tier — it's the
// Starter-level subset of Pro's saved "custom AI rewriter prompts".
export default function BrandVoiceSection({ domain }) {
  const qc = useQueryClient();
  const [voice, setVoice] = useState(domain.brandVoice || '');

  useEffect(() => {
    setVoice(domain.brandVoice || '');
  }, [domain._id, domain.brandVoice]);

  const save = useMutation({
    mutationFn: () =>
      api
        .put(`/api/domains/${domain._id}`, { brandVoice: voice })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['domains'] });
      toast.success('Brand voice saved');
    },
    onError: (err) =>
      toast.error(err?.response?.data?.message || 'Could not save brand voice'),
  });

  const dirty = (domain.brandVoice || '') !== voice;

  return (
    <div className="card">
      <div className="section-header">
        <div className="section-title">
          <span className="section-icon">
            <MessageSquareText className="w-4 h-4" />
          </span>
          Brand voice
          <InfoTip
            title="What is brand voice?"
            body="A short description of how your content should sound — tone, personality, words to use or avoid. The AI rewriter follows it on every rewrite for this domain, without changing your facts."
          />
        </div>
      </div>

      <p className="text-sm text-gray-600 mb-3">
        Describe your tone and style. The AI rewriter applies this to every
        rewrite on <code>{domain.domain}</code> — so the output sounds like you,
        not a generic robot.
      </p>

      <textarea
        className="input min-h-[120px] resize-y"
        maxLength={MAX}
        placeholder="e.g. Warm and direct. Plain English, short sentences, no buzzwords or hype. Speak to small-business owners. British spelling. Avoid exclamation marks."
        value={voice}
        onChange={(e) => setVoice(e.target.value)}
      />
      <div className="flex items-center justify-between mt-2">
        <span className="text-xs text-gray-400">
          {voice.length}/{MAX}
        </span>
        <button
          type="button"
          className="btn-primary"
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate()}
        >
          <Save className="w-4 h-4" />
          {save.isPending ? 'Saving…' : 'Save brand voice'}
        </button>
      </div>
    </div>
  );
}
